import { Plus, X } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { LookupField } from './lookup-field';

/**
 * A connector's config form, generated from the JSON Schema `z.toJSONSchema`
 * exports (step 36a). Supported: string (uri → url input), number, integer,
 * boolean, string enum, arrays of any supported item, nested objects, `default`,
 * `description` as help, `minimum`/`maximum`/`maxItems` as constraints, and
 * `x-lookup` (a connector picker). Anything else edits as raw JSON for that
 * property, never a blank. The server stays the judge of validity.
 */

export type JsonSchema = Record<string, unknown>;

/** A JSON textarea holding text that does not parse. The dialog refuses to save it. */
export const INVALID_JSON: unique symbol = Symbol('invalid-json');

export const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const num = (s: JsonSchema, key: string) => (typeof s[key] === 'number' ? s[key] : undefined);

function propertiesOf(schema: JsonSchema): Record<string, JsonSchema> | null {
  if (schema['type'] !== 'object' || !isRecord(schema['properties'])) return null;
  const out: Record<string, JsonSchema> = {};
  for (const [key, value] of Object.entries(schema['properties'])) {
    if (!isRecord(value)) return null;
    out[key] = value;
  }
  return out;
}

const requiredOf = (schema: JsonSchema): string[] =>
  Array.isArray(schema['required'])
    ? schema['required'].filter((k): k is string => typeof k === 'string')
    : [];

type Kind = 'lookup' | 'enum' | 'string' | 'number' | 'boolean' | 'array' | 'object' | 'json';

export function kindOf(schema: JsonSchema): Kind {
  if (typeof schema['x-lookup'] === 'string') return 'lookup';
  const values = schema['enum'];
  if (Array.isArray(values)) {
    return values.length > 0 && values.every((v) => typeof v === 'string') ? 'enum' : 'json';
  }
  switch (schema['type']) {
    case 'string':
      return 'string';
    case 'number':
    case 'integer':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'array':
      return isRecord(schema['items']) && kindOf(schema['items']) !== 'json' ? 'array' : 'json';
    case 'object':
      return propertiesOf(schema) ? 'object' : 'json';
    default:
      return 'json';
  }
}

/** Defaults filled in; a required object or array starts empty; the rest stays unset. */
export function initialValue(schema: JsonSchema, required = true): unknown {
  if ('default' in schema) return structuredClone(schema['default']);
  const properties = propertiesOf(schema);
  if (properties) {
    if (!required) return undefined;
    const needed = requiredOf(schema);
    const out: Record<string, unknown> = {};
    for (const [key, property] of Object.entries(properties)) {
      const value = initialValue(property, needed.includes(key));
      if (value !== undefined) out[key] = value;
    }
    return out;
  }
  if (schema['type'] === 'array') return required ? [] : undefined;
  return undefined;
}

/** True when some JSON textarea in `value` does not parse. */
export function hasInvalidJson(value: unknown): boolean {
  if (value === INVALID_JSON) return true;
  if (Array.isArray(value)) return value.some(hasInvalidJson);
  return isRecord(value) && Object.values(value).some(hasInvalidJson);
}

function useFieldLabel() {
  const { t } = useTranslation('sources');
  return (name: string, schema: JsonSchema) =>
    t(`config.${name}`, {
      defaultValue: typeof schema['title'] === 'string' ? schema['title'] : name,
    });
}

function constraintHint(
  t: ReturnType<typeof useTranslation<'sources'>>['t'],
  schema: JsonSchema,
): string | undefined {
  const min = num(schema, 'minimum');
  const max = num(schema, 'maximum');
  if (min !== undefined && max !== undefined) return t('form.range', { min, max });
  if (min !== undefined) return t('form.min', { min });
  if (max !== undefined) return t('form.max', { max });
  return undefined;
}

/** Label, control, help line and error, tied together for assistive tech. */
function FieldShell({
  id,
  label,
  optional,
  help,
  error,
  children,
}: {
  id: string;
  label: string;
  optional: boolean;
  help: string | undefined;
  error: string | undefined;
  children: ReactNode;
}) {
  const { t } = useTranslation('sources');
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        {optional && (
          <span className="font-normal text-muted-foreground"> ({t('form.optional')})</span>
        )}
      </Label>
      {children}
      {help && (
        <p id={`${id}-help`} className="text-xs text-muted-foreground">
          {help}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/** aria wiring for a control inside FieldShell. */
export function describedBy(id: string, help: string | undefined, error: string | undefined) {
  const ids = [help ? `${id}-help` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ');
  return {
    ...(ids ? { 'aria-describedby': ids } : {}),
    ...(error ? { 'aria-invalid': true } : {}),
  };
}

const selectClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 aria-[invalid=true]:border-destructive';

const textareaClass =
  'flex min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs leading-relaxed focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 aria-[invalid=true]:border-destructive';

function JsonInput({
  id,
  value,
  onChange,
  aria,
  label,
}: {
  id?: string;
  value: unknown;
  onChange: (value: unknown) => void;
  aria: Record<string, unknown>;
  label?: string;
}) {
  const { t } = useTranslation('sources');
  const [text, setText] = useState(() =>
    value === undefined || value === INVALID_JSON ? '' : JSON.stringify(value, null, 2),
  );
  const [bad, setBad] = useState(value === INVALID_JSON);
  return (
    <>
      <textarea
        id={id}
        aria-label={label}
        {...aria}
        {...(bad ? { 'aria-invalid': true } : {})}
        className={textareaClass}
        spellCheck={false}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setBad(false);
          if (e.target.value.trim() === '') return onChange(undefined);
          try {
            onChange(JSON.parse(e.target.value));
          } catch {
            setBad(true);
            onChange(INVALID_JSON);
          }
        }}
      />
      {bad && <p className="text-xs text-destructive">{t('form.jsonInvalid')}</p>}
    </>
  );
}

interface PropertyProps {
  connectorId: string;
  name: string;
  schema: JsonSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  required: boolean;
  error?: string | undefined;
}

function PropertyField({
  connectorId,
  name,
  schema,
  value,
  onChange,
  required,
  error,
}: PropertyProps) {
  const { t } = useTranslation('sources');
  const labelFor = useFieldLabel();
  const id = useId();
  const label = labelFor(name, schema);
  const kind = kindOf(schema);
  const description = typeof schema['description'] === 'string' ? schema['description'] : '';
  const help =
    [
      description,
      kind === 'number' ? constraintHint(t, schema) : undefined,
      kind === 'json' ? t('form.jsonHint') : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;
  const aria = describedBy(id, help, error);
  const optional = !required;

  if (kind === 'lookup') {
    return (
      <LookupField
        connectorId={connectorId}
        lookup={String(schema['x-lookup'])}
        schema={schema}
        label={label}
        optional={optional}
        help={help}
        error={error}
        value={value}
        onChange={onChange}
      />
    );
  }

  if (kind === 'object') {
    return (
      <ObjectFieldset
        connectorId={connectorId}
        legend={label}
        optional={optional}
        schema={schema}
        value={value}
        error={error}
        // An optional object left empty is omitted, not sent as {}.
        onChange={(next) => onChange(Object.keys(next).length === 0 && optional ? undefined : next)}
      />
    );
  }

  if (kind === 'array') {
    return (
      <ArrayFieldset
        connectorId={connectorId}
        legend={label}
        optional={optional}
        schema={schema}
        value={value}
        error={error}
        help={help}
        onChange={onChange}
      />
    );
  }

  if (kind === 'boolean') {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <input
            id={id}
            type="checkbox"
            className="h-4 w-4 accent-primary"
            {...aria}
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
          />
          <Label htmlFor={id}>{label}</Label>
        </div>
        {help && (
          <p id={`${id}-help`} className="pl-6 text-xs text-muted-foreground">
            {help}
          </p>
        )}
        {error && (
          <p id={`${id}-error`} className="pl-6 text-xs text-destructive">
            {error}
          </p>
        )}
      </div>
    );
  }

  let control: ReactNode;
  if (kind === 'enum') {
    const options = schema['enum'] as string[];
    control = (
      <select
        id={id}
        {...aria}
        className={selectClass}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
      >
        {(optional || typeof value !== 'string') && <option value="">—</option>}
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  } else if (kind === 'number') {
    const integer = schema['type'] === 'integer';
    control = (
      <Input
        id={id}
        {...aria}
        type="number"
        inputMode={integer ? 'numeric' : 'decimal'}
        className="tabular-nums"
        min={num(schema, 'minimum')}
        max={num(schema, 'maximum')}
        step={integer ? 1 : 'any'}
        value={typeof value === 'number' ? value : ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      />
    );
  } else if (kind === 'string') {
    const url = schema['format'] === 'uri';
    control = (
      <Input
        id={id}
        {...aria}
        type={url ? 'url' : 'text'}
        spellCheck={url ? false : undefined}
        className={url ? 'font-mono' : undefined}
        maxLength={num(schema, 'maxLength')}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
      />
    );
  } else {
    control = <JsonInput id={id} value={value} onChange={onChange} aria={aria} />;
  }

  return (
    <FieldShell id={id} label={label} optional={optional} help={help} error={error}>
      {control}
    </FieldShell>
  );
}

function Fieldset({
  legend,
  optional,
  aside,
  error,
  help,
  children,
}: {
  legend: string;
  optional: boolean;
  aside?: ReactNode;
  error: string | undefined;
  help?: string | undefined;
  children: ReactNode;
}) {
  const { t } = useTranslation('sources');
  return (
    <fieldset
      className={cn(
        'space-y-3 rounded-lg border p-3',
        error && 'border-destructive/60 ring-1 ring-destructive/30',
      )}
    >
      <legend className="flex items-center gap-2 px-1 text-sm font-medium">
        {legend}
        {optional && (
          <span className="font-normal text-muted-foreground">({t('form.optional')})</span>
        )}
        {aside}
      </legend>
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </fieldset>
  );
}

function ObjectFieldset({
  connectorId,
  legend,
  optional,
  schema,
  value,
  error,
  onChange,
}: {
  connectorId: string;
  legend: string;
  optional: boolean;
  schema: JsonSchema;
  value: unknown;
  error?: string | undefined;
  onChange: (value: Record<string, unknown>) => void;
}) {
  return (
    <Fieldset legend={legend} optional={optional} error={error}>
      <ObjectFields connectorId={connectorId} schema={schema} value={value} onChange={onChange} />
    </Fieldset>
  );
}

function ObjectFields({
  connectorId,
  schema,
  value,
  onChange,
  errors,
}: {
  connectorId: string;
  schema: JsonSchema;
  value: unknown;
  onChange: (value: Record<string, unknown>) => void;
  errors?: Record<string, string> | undefined;
}) {
  const properties = propertiesOf(schema) ?? {};
  const required = requiredOf(schema);
  const current = isRecord(value) ? value : {};
  const set = (key: string, next: unknown) => {
    const copy = { ...current };
    if (next === undefined) delete copy[key];
    else copy[key] = next;
    onChange(copy);
  };
  return (
    <>
      {Object.entries(properties).map(([key, property]) => (
        <PropertyField
          key={key}
          connectorId={connectorId}
          name={key}
          schema={property}
          value={current[key]}
          required={required.includes(key)}
          error={errors?.[key]}
          onChange={(next) => set(key, next)}
        />
      ))}
    </>
  );
}

function blankItem(items: JsonSchema): unknown {
  const value = initialValue(items);
  if (value !== undefined) return value;
  if (items['type'] === 'string') return '';
  if (items['type'] === 'boolean') return false;
  return null;
}

function ArrayFieldset({
  connectorId,
  legend,
  optional,
  schema,
  value,
  error,
  help,
  onChange,
}: {
  connectorId: string;
  legend: string;
  optional: boolean;
  schema: JsonSchema;
  value: unknown;
  error?: string | undefined;
  help?: string | undefined;
  onChange: (value: unknown[]) => void;
}) {
  const { t } = useTranslation('sources');
  const items = schema['items'] as JsonSchema;
  const list = Array.isArray(value) ? value : [];
  const max = num(schema, 'maxItems');
  const full = max !== undefined && list.length >= max;
  const objects = kindOf(items) === 'object';
  const setAt = (i: number, next: unknown) =>
    onChange(list.map((item, j) => (j === i ? (next ?? blankItem(items)) : item)));
  const removeAt = (i: number) => onChange(list.filter((_, j) => j !== i));

  return (
    <Fieldset
      legend={legend}
      optional={optional}
      error={error}
      help={help}
      aside={
        max !== undefined && (
          <span className="font-mono text-xs font-normal tabular-nums text-muted-foreground">
            {t('form.count', { count: list.length, max })}
          </span>
        )
      }
    >
      {list.length === 0 && <p className="text-sm text-muted-foreground">{t('form.none')}</p>}
      {list.length > 0 && (
        <ul className="space-y-2">
          {list.map((item, i) => {
            const index = i + 1;
            const remove = (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-10 w-10 shrink-0 p-0"
                aria-label={t('form.removeItem', { index, field: legend })}
                onClick={() => removeAt(i)}
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </Button>
            );
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: items have no identity of their own; the index is their position.
              <li key={i} className="animate-rise">
                {objects ? (
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <ObjectFieldset
                        connectorId={connectorId}
                        legend={t('form.item', { index })}
                        optional={false}
                        schema={items}
                        value={item}
                        onChange={(next) => setAt(i, next)}
                      />
                    </div>
                    <div className="pt-3">{remove}</div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <ItemControl
                        connectorId={connectorId}
                        schema={items}
                        value={item}
                        label={t('form.itemOf', { field: legend, index })}
                        onChange={(next) => setAt(i, next)}
                      />
                    </div>
                    {remove}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={full}
        aria-label={t('form.addLabel', { field: legend })}
        onClick={() => onChange([...list, blankItem(items)])}
      >
        <Plus aria-hidden="true" className="h-4 w-4" />
        {t('form.add')}
      </Button>
    </Fieldset>
  );
}

/** One scalar array item: the same controls, labelled by aria-label instead of a visible label. */
function ItemControl({
  connectorId,
  schema,
  value,
  label,
  onChange,
}: {
  connectorId: string;
  schema: JsonSchema;
  value: unknown;
  label: string;
  onChange: (value: unknown) => void;
}) {
  const kind = kindOf(schema);
  if (kind === 'string' || kind === 'number') {
    const isNumber = kind === 'number';
    return (
      <Input
        aria-label={label}
        type={isNumber ? 'number' : schema['format'] === 'uri' ? 'url' : 'text'}
        className={isNumber ? 'tabular-nums' : undefined}
        min={num(schema, 'minimum')}
        max={num(schema, 'maximum')}
        maxLength={num(schema, 'maxLength')}
        value={typeof value === 'string' || typeof value === 'number' ? value : ''}
        onChange={(e) =>
          onChange(
            isNumber ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value,
          )
        }
      />
    );
  }
  // Booleans, enums, lookups and nested arrays inside an array: the generic field, named by position.
  return (
    <PropertyField
      connectorId={connectorId}
      name={label}
      schema={schema}
      value={value}
      required
      onChange={onChange}
    />
  );
}

/**
 * The whole config. `errors` is keyed by top-level property (the first segment
 * of a server issue path). A schema that is not an object with properties edits
 * as one JSON value.
 */
export function SchemaForm({
  connectorId,
  schema,
  value,
  onChange,
  errors,
}: {
  connectorId: string;
  schema: JsonSchema;
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  errors?: Record<string, string> | undefined;
}) {
  const { t } = useTranslation('sources');
  if (!propertiesOf(schema)) {
    return (
      <div className="space-y-1.5">
        <JsonInput
          value={value}
          label={t('dialog.config')}
          aria={{}}
          // A config is an object: anything else is held as invalid until the text is fixed.
          onChange={(next) =>
            onChange(isRecord(next) ? next : next === undefined ? {} : { invalid: INVALID_JSON })
          }
        />
        <p className="text-xs text-muted-foreground">{t('form.jsonHint')}</p>
      </div>
    );
  }
  return (
    <ObjectFields
      connectorId={connectorId}
      schema={schema}
      value={value}
      onChange={onChange}
      errors={errors}
    />
  );
}
